import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  getInstagramConversations,
  getInstagramConversationDetail,
  getInstagramMessages,
  markInstagramConversationRead,
  sendInstagramMessage,
  reactToInstagramMessage,
} from '../api';

export const handleInstagramError = (error) => {
  if (error?.response) {
    const status = error.response.status;
    if (status >= 500) {
      toast.error('Something went wrong with Instagram service.');
    } else if (status >= 400 && status < 500) {
      const msg =
        error.response.data?.message ||
        error.response.data?.error ||
        error.response.data?.detail ||
        'An Instagram API error occurred.';
      toast.error(msg);
    }
  } else {
    toast.error('Network error. Please check your connection.');
  }
};

export function useInstagramConversations(
  companyId,
  params = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ['instagram-conversations', companyId, params],
    queryFn: () => getInstagramConversations(companyId, params),
    enabled: !!companyId && enabled,
  });
}

export function useInfiniteInstagramConversations(
  companyId,
  params = {},
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: ['instagram-conversations-infinite', companyId, params],
    queryFn: ({ pageParam = 1 }) =>
      getInstagramConversations(companyId, { ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (!lastPage?.next) return undefined;
      try {
        const url = new URL(lastPage.next);
        return url.searchParams.get('page');
      } catch {
        return undefined;
      }
    },
    enabled: !!companyId && enabled,
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: enabled ? 20000 : false,
  });
}

export function useInstagramConversationDetail(
  companyId,
  conversationId,
  enabled = true,
) {
  return useQuery({
    queryKey: ['instagram-conversation-detail', companyId, conversationId],
    queryFn: () =>
      getInstagramConversationDetail({ companyId, conversationId }),
    enabled: !!companyId && !!conversationId && enabled,
  });
}

export function useCustomInstagramMessages(
  companyId,
  conversationId,
  params = {},
  enabled = true,
) {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isFetchingOlder, setIsFetchingOlder] = useState(false);
  const [isFetchingNewer, setIsFetchingNewer] = useState(false);

  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasOlder, setHasOlder] = useState(false);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const getPageParam = (urlStr) => {
    if (!urlStr) return null;
    try {
      const url = new URL(urlStr);
      return url.searchParams.get('page');
    } catch {
      return null;
    }
  };

  const fetchPage = async (pageParam, mergeStrategy = 'append_newer') => {
    if (!enabled || !companyId || !conversationId) return null;
    try {
      const data = await getInstagramMessages({
        companyId,
        conversationId,
        params,
        pageParam,
      });

      const newRawGroups = data.results || [];
      let incomingMessages = [];
      newRawGroups.forEach((group) => {
        if (group.messages) {
          incomingMessages = [...incomingMessages, ...group.messages];
        } else if (group.id) {
          incomingMessages.push(group);
        }
      });

      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => String(m.id)));
        const uniqueIncoming = incomingMessages.filter(
          (m) => !existingIds.has(String(m.id)),
        );

        if (mergeStrategy === 'initial') {
          return incomingMessages;
        } else if (mergeStrategy === 'prepend_older') {
          return [...prev, ...uniqueIncoming];
        } else if (mergeStrategy === 'append_newer') {
          return [...uniqueIncoming, ...prev];
        }
        return prev;
      });

      return data;
    } catch (error) {
      console.error('Failed to fetch Instagram messages:', error);
      handleInstagramError(error);
      return null;
    }
  };

  // Initial Load
  useEffect(() => {
    let isMounted = true;

    if (enabled && companyId && conversationId) {
      const loadInitial = async () => {
        setIsLoadingInitial(true);
        setMessages([]);
        setNextPageUrl(null);
        setHasOlder(false);

        const [data] = await Promise.all([
          fetchPage(null, 'initial'),
          markInstagramConversationRead({ companyId, conversationId }).catch(
            console.error,
          ),
        ]);

        queryClient.invalidateQueries({
          queryKey: ['instagram-conversations', companyId],
        });

        if (isMounted && data) {
          if (data.next) {
            setNextPageUrl(data.next);
            setHasOlder(true);
          }
        }
        if (isMounted) setIsLoadingInitial(false);
      };
      loadInitial();
    } else {
      setMessages([]);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, conversationId, enabled]);

  // Polling for Newer Messages
  const pollTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!enabled || !companyId || !conversationId) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      return;
    }

    const poll = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const msgs = messagesRef.current;
        let sinceId = undefined;
        if (msgs && msgs.length > 0) {
          const maxId = Math.max(
            ...msgs.map((m) => parseInt(m.id, 10)).filter((id) => !isNaN(id)),
          );
          if (maxId > -Infinity) {
            sinceId = maxId;
          }
        }

        const queryParams = { flat: true, order: 'asc' };
        if (sinceId !== undefined) {
          queryParams.since_id = sinceId;
        }

        const data = await getInstagramMessages({
          companyId,
          conversationId,
          params: queryParams,
          signal: controller.signal,
        });

        let incomingMessages = Array.isArray(data) ? data : data.results || [];

        if (incomingMessages.length > 0) {
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => String(m.id)));
            const uniqueIncoming = incomingMessages.filter(
              (m) => !existingIds.has(String(m.id)),
            );

            if (uniqueIncoming.length === 0) return prev;

            const reversedIncoming = [...uniqueIncoming].reverse();
            return [...reversedIncoming, ...prev];
          });

          markInstagramConversationRead({ companyId, conversationId }).catch(
            console.error,
          );
          queryClient.invalidateQueries({
            queryKey: ['instagram-conversations', companyId],
          });
        }
      } catch (error) {
        if (
          error.code === 'ERR_CANCELED' ||
          error.name === 'CanceledError' ||
          error.name === 'AbortError'
        ) {
          return;
        }
        console.error('Error polling Instagram messages:', error);
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    };

    pollTimerRef.current = setInterval(poll, 15000);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [companyId, conversationId, enabled, queryClient]);

  const fetchOlderMessages = useCallback(async () => {
    if (!hasOlder || isFetchingOlder || !nextPageUrl) return;

    setIsFetchingOlder(true);
    const pageParam = getPageParam(nextPageUrl);
    const data = await fetchPage(pageParam, 'prepend_older');

    if (data) {
      setNextPageUrl(data.next);
      setHasOlder(!!data.next);
    }
    setIsFetchingOlder(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasOlder,
    isFetchingOlder,
    nextPageUrl,
    companyId,
    conversationId,
    enabled,
  ]);

  const fetchNewerMessages = useCallback(async () => {
    if (isFetchingNewer) return;
    setIsFetchingNewer(true);
    await Promise.all([
      fetchPage(null, 'append_newer'),
      markInstagramConversationRead({ companyId, conversationId }).catch(
        console.error,
      ),
    ]);
    queryClient.invalidateQueries({
      queryKey: ['instagram-conversations', companyId],
    });
    setIsFetchingNewer(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingNewer, companyId, conversationId, enabled]);

  return {
    messages,
    isLoadingInitial,
    isFetchingOlder,
    isFetchingNewer,
    fetchOlderMessages,
    fetchNewerMessages,
    hasOlder,
  };
}

export function useSendInstagramMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendInstagramMessage,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['instagram-messages', variables.companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['instagram-conversations-infinite', variables.companyId],
      });
    },
    onError: (error) => {
      handleInstagramError(error);
    },
  });
}

export function useReactToInstagramMessage() {
  return useMutation({
    mutationFn: reactToInstagramMessage,
    onError: (error) => {
      handleInstagramError(error);
    },
  });
}
